import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Bytes } from "@/components/bytes";
import { describeExpiry, StatCell, StatStrip } from "@/components/stat-strip";
import { formatDate } from "@/lib/locale-format";
import { formatAmount } from "@/lib/pricing";
import type { MySetupSite, OrderResp } from "@/lib/types";

interface Props {
  order: OrderResp;
  /**
   * The buyer's site: `undefined` while the paid order's setup data is
   * still loading, `null` once loaded with no site created yet.
   */
  site?: MySetupSite | null;
}

function Dot({ className }: { className: string }) {
  return <span className={`h-2 w-2 rounded-full ${className}`} />;
}

function StatusCell({ dot, children }: { dot: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <StatCell label={t("orderStatus.status")} className="col-span-2 lg:col-span-1">
      <span className="inline-flex items-center gap-1.5">
        <Dot className={dot} />
        {children}
      </span>
    </StatCell>
  );
}

/**
 * At-a-glance strip for the buyer's order dashboard. Cells shift with the
 * order's stage: payment facts while awaiting, page facts once it's live.
 */
export function OrderStatusStrip({ order, site }: Props) {
  const { t } = useTranslation();
  const purchased = order.createdAt ? formatDate(new Date(order.createdAt)) : "-";
  const total = formatAmount(order.snapshot.amountMinor, order.snapshot.currency);

  const { storageGb, months } = order.snapshot;
  // Size/Duration only render when the package has them, so column count
  // and mobile row parity both depend on how many exist.
  const extras = (storageGb !== null ? 1 : 0) + (months !== null ? 1 : 0);
  const stripCols = (base: 4 | 5) => (base + extras) as 4 | 5 | 6 | 7;
  // Status and Package each take a full row on mobile; the cells after them
  // pair up two per row, so an odd tail stretches its last cell to avoid a
  // hole in the strip.
  const tailSpan = (tail: number) =>
    (tail + extras) % 2 === 1 ? "col-span-2 lg:col-span-1" : undefined;

  const packageCells = (
    <>
      <StatCell label={t("orderStatus.package")} className="col-span-2 lg:col-span-1">
        {order.snapshot.tierName}
      </StatCell>
      {storageGb !== null && <StatCell label={t("orderStatus.size")}>{storageGb} GB</StatCell>}
      {months !== null && (
        <StatCell label={t("orderStatus.duration")}>
          {t("orderStatus.months", { count: months })}
        </StatCell>
      )}
    </>
  );

  if (order.status === "cancelled") {
    return (
      <StatStrip columns={stripCols(4)}>
        <StatusCell dot="bg-muted-foreground">{t("orderStatus.cancelled")}</StatusCell>
        {packageCells}
        <StatCell label={t("orderStatus.purchased")}>{purchased}</StatCell>
        <StatCell label={t("orderStatus.total")} className={tailSpan(2)}>
          {total}
        </StatCell>
      </StatStrip>
    );
  }

  if (order.status === "awaiting_payment") {
    const due = order.payment?.dueDate ? new Date(order.payment.dueDate) : null;
    return (
      <StatStrip columns={stripCols(5)}>
        <StatusCell dot="bg-amber-500">{t("orderStatus.awaitingPayment")}</StatusCell>
        {packageCells}
        <StatCell label={t("orderStatus.purchased")}>{purchased}</StatCell>
        <StatCell label={t("orderStatus.dueDate")}>
          {due ? (
            <span className={due.getTime() < Date.now() ? "text-destructive" : undefined}>
              {formatDate(due)}
            </span>
          ) : (
            "-"
          )}
        </StatCell>
        <StatCell label={t("orderStatus.totalToPay")} className={tailSpan(3)}>
          {total}
        </StatCell>
      </StatStrip>
    );
  }

  // Paid. Before the site exists the strip stays about the order; after,
  // it becomes the page's health row.
  if (!site) {
    return (
      <StatStrip columns={stripCols(4)}>
        <StatusCell dot="bg-emerald-500">{t("orderStatus.paid")}</StatusCell>
        {packageCells}
        <StatCell label={t("orderStatus.purchased")}>{purchased}</StatCell>
        <StatCell label={t("orderStatus.totalPaid")} className={tailSpan(2)}>
          {total}
        </StatCell>
      </StatStrip>
    );
  }

  const expiry = describeExpiry(site.expiresAt ? new Date(site.expiresAt) : null);

  return (
    <StatStrip columns={stripCols(4)}>
      <StatusCell dot="bg-emerald-500">{t("orderStatus.active")}</StatusCell>
      {packageCells}

      <StatCell label={t("orderStatus.uploads")}>
        {site.originalFileCount !== undefined ? (
          <span className="inline-flex items-baseline gap-1.5">
            <span>{t("common.files", { count: site.originalFileCount })}</span>
            <span className="text-xs text-muted-foreground">
              <Bytes value={site.originalBytes ?? 0} />
            </span>
          </span>
        ) : (
          "-"
        )}
      </StatCell>

      <StatCell label={t("orderStatus.expires")} className={tailSpan(2)}>
        <span className={expiry.className}>{expiry.label}</span>
      </StatCell>
    </StatStrip>
  );
}
